
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { authorize } from '../../../services/authService';
import domtoimage from 'dom-to-image';
import {getFileSign, storeFile} from "../../../utils/indexedDB";
import {getFullName, signDocument} from "../../../services/signService";
import {useNavigate} from "react-router-dom";
//import logoSignature from "../../../assets/logo_signature.png"
import {ComponentAlerts} from "../ComponentAlerts";
import SignatureAppearance from "../appearance/SignatureAppearance";
import {applySignaturePageMode} from "../../../utils/signatureRequest";
import {freezeComputedSizes, isSafari, waitImagesReady} from "../../../utils/imageUtils";
import {
  getPreviousSignaturePage,
  isAllPagesModeEnabled,
  SIGNATURE_PAGE_MODE_EVENT,
  updateAllPagesMode
} from '../../../utils/signaturePageMode';
import {deleteSignatureAppearance, getSignatureAppearances, updateSignatureAppearance} from "../../../services/signatureAppearanceService";
import { getBrandLogoPng } from '../../../branding/brandings';
// --- WYSIWYG helpers ---
const PX_TO_PT = 72 / 96;
const getDPR = () => Math.max(1, (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1);
const MIN_SIGNATURE_TEMPLATE_HOST_WIDTH_PX = 520;
const MAX_SIGNATURE_TEMPLATE_HOST_WIDTH_PX = 1800;
const logoSrc = getBrandLogoPng();
const pad2 = (value) => String(value).padStart(2, '0');

async function waitFontsAndImages(root) {
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {}
  const imgs = Array.from(root?.querySelectorAll('img') || []);
  await Promise.all(imgs.map(i => i.decode?.().catch(() => null)));
}

async function captureNodeToPNG(node) {
  if (!node) {
    throw new Error('La vista previa de la apariencia no esta disponible.');
  }
  await waitFontsAndImages(node);
  await waitImagesReady(node);
  const rect = node.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) {
    throw new Error('La vista previa de la apariencia no tiene dimensiones validas.');
  }
  const dpr = getDPR()

  const restore = freezeComputedSizes(node);
    try {
        const imgs = Array.from(node?.querySelectorAll('img') || []);
        if (imgs.some(img => !(img.complete && img.naturalWidth > 0))) {
          await new Promise(r => setTimeout(r, 150));
        }
        const doCapture = () => domtoimage.toPng(node, {
              bgcolor: null,
              width:  Math.round(rect.width  * dpr),   // raster a DPR
              height: Math.round(rect.height * dpr),
              style: {
              transform: `scale(${dpr})`,
                  transformOrigin: 'top left',
                  width:  `${Math.round(rect.width)}px`,
                  height: `${Math.round(rect.height)}px`,
                }
        });
        if (isSafari()) {
          // Safari: warm-up render to avoid missing external images on first attempt
          try { await doCapture(); } catch {}
          await new Promise(r => setTimeout(r, 150));
        }
        let lastError = null;
        for (let attempt = 1; attempt <= 3; attempt += 1) {
          try {
            return await doCapture();
          } catch (error) {
            lastError = error;
            if (attempt < 3) {
              await waitImagesReady(node);
              await new Promise(r => setTimeout(r, 150 * attempt));
            }
          }
        }
        if (lastError instanceof Error) throw lastError;
        throw new Error('No se pudieron cargar todos los recursos de la apariencia.');
      } finally {
        restore(); // volver al estado normal del DOM
      }
}

const formatDateTimeWithTZ = (date) => {
  if (!date) return '';
  const day = pad2(date.getDate());
  const month = pad2(date.getMonth() + 1);
  const year = date.getFullYear();
  const hh = pad2(date.getHours());
  const mm = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  const offsetMin = -date.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const offH = pad2(Math.floor(abs / 60));
  const offM = pad2(abs % 60);
  return `${day}/${month}/${year} ${hh}:${mm}:${ss} ${sign}${offH}'${offM}'`;
};

const refreshCustomAppearanceDate = (htmlTemplate, configAppearance) => {
  const nextConfigAppearance = {
    ...(configAppearance || {}),
    text: {
      ...((configAppearance && configAppearance.text) || {})
    }
  };

  if (typeof htmlTemplate !== 'string' || !htmlTemplate.includes('data-appearance-date')) {
    return { htmlTemplate, configAppearance: nextConfigAppearance, dateUpdated: false };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlTemplate, 'text/html');
    const dateNodes = doc.querySelectorAll('[data-appearance-date]');
    if (!dateNodes.length) {
      return { htmlTemplate, configAppearance: nextConfigAppearance, dateUpdated: false };
    }

    const now = new Date();
    const nextDateText = formatDateTimeWithTZ(now);
    dateNodes.forEach((node) => {
      node.textContent = nextDateText;
    });
    nextConfigAppearance.text.dateSelectedAtIso = now.toISOString();
    return {
      htmlTemplate: doc.body.innerHTML,
      configAppearance: nextConfigAppearance,
      dateUpdated: true
    };
  } catch {
    return { htmlTemplate, configAppearance: nextConfigAppearance, dateUpdated: false };
  }
};

const renderHtmlTemplateToPNG = async (htmlTemplate, hostWidthPx = 0) => {
  if (typeof htmlTemplate !== 'string' || !htmlTemplate.trim()) {
    return null;
  }

  const widthNum = Number(hostWidthPx);
  const baseWidth = Number.isFinite(widthNum) && widthNum > 0 ? Math.round(widthNum) : 420;
  const hostWidth = Math.max(MIN_SIGNATURE_TEMPLATE_HOST_WIDTH_PX, baseWidth);
  const mount = document.createElement('div');
  mount.style.position = 'fixed';
  mount.style.left = '-10000px';
  mount.style.top = '0';
  mount.style.pointerEvents = 'none';
  mount.style.zIndex = '-1';
  mount.style.background = '#ffffff';
  mount.style.width = `${hostWidth}px`;
  mount.innerHTML = `
    <div class="a-view">
      <div class="appearance-html-preview">
        <div class="appearance-html-preview__inner">${htmlTemplate}</div>
      </div>
    </div>
  `;
  document.body.appendChild(mount);

  try {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const target = mount.querySelector('.appearance-html-preview__inner') || mount.firstElementChild || mount;
    if (!target) return null;
    const preferredWidth = Math.ceil(
      Number(target.scrollWidth) ||
      Number(target.getBoundingClientRect().width) ||
      hostWidth
    );
    if (preferredWidth > hostWidth) {
      const expandedWidth = Math.min(MAX_SIGNATURE_TEMPLATE_HOST_WIDTH_PX, preferredWidth + 8);
      mount.style.width = `${expandedWidth}px`;
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
    return await captureNodeToPNG(target);
  } finally {
    mount.remove();
  }
};

const getImageDimensionsFromDataUrl = async (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({
      width: Number(img.naturalWidth || img.width || 0),
      height: Number(img.naturalHeight || img.height || 0),
    });
    img.onerror = reject;
    img.src = dataUrl;
  });

const ModalSeleccionarApariencia = () => {

  const { t } = useTranslation();
  const container = useRef(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [option, setOption] = useState('0');
  const fileName = sessionStorage.getItem('fileName');
  const appearanceRef = useRef();
  const minScale = 0.5; // La firma puede reducirse a la mitad
  const maxScale = 1.3;
  const [pageInput, setPageInput] = useState('1');     // X (string)
  const [numPages, setNumPages] = useState(null);      // N (number | null)
  const [pageError, setPageError] = useState(false);   // flag de error para highlight
  const [allPages, setAllPages] = useState(isAllPagesModeEnabled);
  const [savedAppearances, setSavedAppearances] = useState([]);

  useEffect(() => {
    const syncPageMode = (event) => {
      const enabled = Boolean(event?.detail?.allPages);
      setAllPages(enabled);
      if (!enabled) {
        setPageInput(String(event?.detail?.previousPage || 1));
      }
      setPageError(false);
    };

    window.addEventListener(SIGNATURE_PAGE_MODE_EVENT, syncPageMode);
    return () => window.removeEventListener(SIGNATURE_PAGE_MODE_EVENT, syncPageMode);
  }, []);

  const unlockBodyAfterModalClose = () => {
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  };

    const loadAppearances = async () => {
        try {
            const list = await getSignatureAppearances();
            const next = Array.isArray(list) ? list : [];
            setSavedAppearances(next);

            const selected = next.find((item) => item?.isSelected);
            if (selected?.id != null) setOption(String(selected.id));

            return next;
        } catch (err) {
            console.error('No se pudieron cargar las apariencias de firma.', err);
            setSavedAppearances([]);
            return [];
        }
    };

    useEffect(() => {
        const onSyncRefresh = async (e) => {
            try {
                await loadAppearances();
            } finally {
                // ACK al que disparó el evento (ModalDefinirApariencia)
                if (typeof e?.detail?.resolve === 'function') {
                    e.detail.resolve();
                }
            }
        };

        window.addEventListener('refresh-signature-appearances-sync', onSyncRefresh);
        return () => window.removeEventListener('refresh-signature-appearances-sync', onSyncRefresh);
    }, []);

    useEffect(() => {
    loadAppearances();
  }, []);

  useEffect(() => {
    const onRefresh = () => loadAppearances();
    window.addEventListener('refresh-signature-appearances', onRefresh);
    return () => window.removeEventListener('refresh-signature-appearances', onRefresh);
  }, []);

    useEffect(() => {
        const onCreated = async (e) => {
            const created = e?.detail?.created;
            if (!created?.id) return;

            const createdId = String(created.id);

            // 1) UI inmediata: insertar y marcar selección consistente en memoria
            setSavedAppearances((prev) => {
                const exists = prev.some(x => String(x?.id) === createdId);
                const nextBase = exists ? prev : [...prev, created];

                return nextBase.map(item => ({
                    ...item,
                    isSelected: String(item?.id) === createdId
                }));
            });

            setOption(createdId);

            // 2) Persistir selección en backend (para que loadAppearances no te "pise" luego)
            try {
                await updateSignatureAppearance(created.id, { isSelected: true });
            } catch (err) {
                console.error('No se pudo marcar la nueva apariencia como seleccionada.', err);
                // No rompas UX: ya está seleccionado en UI
            }
        };

        window.addEventListener('signature-appearance-created', onCreated);
        return () => window.removeEventListener('signature-appearance-created', onCreated);
    }, []);


    useEffect(() => {
    function handleOpenModal() {
      try {
        const cfg = JSON.parse(atob(sessionStorage.getItem('configs') || ''));
        const v = cfg?.SIGNATURE_APPEARANCE_MODAL;
        const enabled = (v === undefined ? true : !!v);
        if (!enabled) return;
      } catch {}
        loadAppearances();
      setTimeout(() => {
        // Asegura el DOM antes de manipularlo
        const modal = document.getElementById('modalSeleccionarApariencia');
        if (modal) {
          modal.classList.add('show');
          modal.style.display = 'block';
          document.body.classList.add('modal-open');
          // backdrop manual si usas Bootstrap puro
          if (!document.querySelector('.modal-backdrop')) {
            const backdrop = document.createElement('div');
            backdrop.className = 'modal-backdrop fade show';
            document.body.appendChild(backdrop);
          }
        }
      }, 10);

            // Prefill X pidiendo al VISOR la página de la firma (no leemos storage del padre)
                const allPagesEnabled = isAllPagesModeEnabled();
                setAllPages(allPagesEnabled);
                setPageInput(allPagesEnabled ? String(getPreviousSignaturePage()) : '1');
            try {
                const iframe = document.querySelector('.pdfViewerContainer iframe');
                if (iframe?.contentWindow && !allPagesEnabled) {
                    iframe.contentWindow.postMessage({ type: 'PDF_CURRENT_PAGE' }, '*');
                  }
              } catch {}

             // Pedir N al visor
              try {
                const iframe = document.querySelector('.pdfViewerContainer iframe');
                if (iframe?.contentWindow) {
                    iframe.contentWindow.postMessage({ type: 'REQUEST_NUM_PAGES' }, '*');
                  }
              } catch {}
    }
    window.addEventListener('open-signature-appearance-modal', handleOpenModal);
    return () => window.removeEventListener('open-signature-appearance-modal', handleOpenModal);
  }, []);

    // Escuchar respuesta con N
        useEffect(() => {
            const onMsg = (e) => {
                if (e?.data?.type === 'PDF_NUM_PAGES') {
                    const n = Number(e.data?.payload?.numPages || 0);
                   setNumPages(n > 0 ? n : null);
                  }
              if (e?.data?.type === 'PDF_CURRENT_PAGE') {
                        const p = Number(e.data?.payload?.page || 0);
                        if (p > 0 && !isAllPagesModeEnabled()) setPageInput(String(p));
                      }
              };
            window.addEventListener('message', onMsg);
            return () => window.removeEventListener('message', onMsg);
          }, []);

        // Solo dígitos
            const sanitizeInt = (v) => String(v).replace(/[^\d]/g, '');
    const onPageChange = (e) => {
        const v = sanitizeInt(e.target.value);
        setPageInput(v);
        if (pageError) setPageError(false);
      };
    const onAllPagesChange = (e) => {
        const enabled = e.target.checked;
        const previousPage = updateAllPagesMode(enabled, pageInput);
        setAllPages(enabled);
        if (!enabled) setPageInput(String(previousPage));
        setPageError(false);
      };
    const onPageKeyDown = (e) => {
        const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Home','End','Tab','Enter'];
        if (allowed.includes(e.key)) {
            if (e.key === 'Enter') handleAccept();
            return;
          }
        if (!/^\d$/.test(e.key)) e.preventDefault();
      };

// Obtener el nombre del usuario del sessionStorage
  const getUserName = () => {
    const toProperCase = (name) => {
      if (!name) return "Usuario";
      return name
          .split(/[\s]+/)
          .map(
              (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');
    };
    const userInfo = sessionStorage.getItem('userinfo');
    if (userInfo) {
      const parsedUserInfo = JSON.parse(userInfo);
      return toProperCase(getFullName(parsedUserInfo));
    }
    return 'Usuario'; // Nombre predeterminado si no se encuentra el valor
  };

  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear());
    return `${day}/${month}/${year}`;
  };

  const handleOptionChange = (e) => {
    const { value } = e.target;
    setOption(value);
    const selected = savedAppearances.find((item) => String(item?.id) === value);
    if (selected) {
      updateSignatureAppearance(selected.id, { isSelected: true })
          .then(() => {
            setSavedAppearances((prev) =>
                prev.map((item) => ({
                  ...item,
                    isSelected: String(item?.id) === String(selected.id)
                }))
            );
          })
          .catch((err) => {
            console.error('No se pudo actualizar la apariencia seleccionada.', err);
          });
    }
  };

  const handleDeleteAppearance = async (e, appearanceId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await deleteSignatureAppearance(appearanceId);
      setSavedAppearances((prev) => prev.filter((item) => item?.id !== appearanceId));
      if (option === String(appearanceId)) {
        setOption('0');
      }
      loadAppearances();
    } catch (err) {
      console.error('No se pudo eliminar la apariencia.', err);
    }
  };

  const handleAppearance = async () => {
    if (option === '0') {
      await fillDefaultAppearance();
    } else {
      fillNoAppearance();
    }
  };

  const fillDefaultAppearance = async () => {
    const element = container.current;
    if (!element) return;

    // Exporta solo la imagen (PNG). No toques upperRight ni pdfWidth/pdfHeight aquí.
    try {
      const dataUrl = await captureNodeToPNG(element);

      const appearance = JSON.parse(sessionStorage.getItem('appearance') || '{}');
      appearance.image = appearance.image || {};
      appearance.image.value = dataUrl;
      sessionStorage.setItem('appearance', JSON.stringify(appearance));

      // (Opcional) refrescar el visor PDF.js si está embebido
      const iframe = document.querySelector('.pdfViewerContainer iframe');
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
            { type: 'UPDATE_SIGNATURE_APPEARANCE', imageData: dataUrl, minScale, maxScale },
            '*'
        );
      }
    } catch (error) {
      console.error('oops, something went wrong!', error);
    }
  };


  const fillNoAppearance = () => {
    // No tocar métricas PDF. Solo limpiar imagenes para que el visor oculte el draggable.
    sessionStorage.removeItem('signatureAppearance');

    const appearance = JSON.parse(sessionStorage.getItem('appearance') || '{}');
    if (appearance?.image) {
      appearance.image.value = ""; // sin imagen
    }
    sessionStorage.setItem('appearance', JSON.stringify(appearance));
  };

  const getConfig = () => {
    const configEncoded = sessionStorage.getItem('configs');
    if (configEncoded) {
      const configDecoded = atob(configEncoded); // Decodificar base64
      return JSON.parse(configDecoded); // Parsear a JSON
    }
    return null; // Retorna null si no existe la configuración
  };

    const getDefaultLogoPositionFromConfigs = () => {
        try {
            const cfg = getConfig();
            return (cfg?.DEFAULT_PAGE_SIGNATURE_LOGO_POSITION || 'BEFORE_TEXT').toString();
        } catch {
            return 'BEFORE_TEXT';
        }
    };


    const signWilthClient = async (e) => {
    e.preventDefault();
    try {
      await handleAppearance(); // fija/limpia imagen según opción

      const pdfFile = await getFileSign(fileName);
      const storedAppearance = sessionStorage.getItem('appearance') || '{}';
      const appearanceData = JSON.parse(storedAppearance);

      const lowerLeft  = appearanceData?.location?.frame?.lowerLeft;
      const upperRight = appearanceData?.location?.frame?.upperRight;

      if (!lowerLeft) throw new Error('lowerLeft ausente en appearance.location.frame');

      const x = Number(lowerLeft.x);
      const y = Number(lowerLeft.y);

      // Tamaño preferente: pdfWidth/pdfHeight
      let width  = Number(appearanceData?.pdfWidth);
      let height = Number(appearanceData?.pdfHeight);

      // Fallback: |UR-LL|
      if (!Number.isFinite(width) || !Number.isFinite(height)) {
        if (upperRight?.x != null && upperRight?.y != null) {
          width  = Math.abs(Number(upperRight.x) - x);
          height = Math.abs(Number(upperRight.y) - y);
        }
      }

      // Guard final (no NaN)
      width  = Number.isFinite(width)  ? width  : 0;
      height = Number.isFinite(height) ? height : 0;

      const pageNumber = appearanceData?.location?.page ?? "last";

      // Imagen (limpia si opción -1)
        let base64Image =
            appearanceData?.image?.value ||
            sessionStorage.getItem('signatureAppearance') ||
            null;

        if (base64Image) {
            base64Image = String(base64Image)
                .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
                .trim();

            if (base64Image === '') {
                base64Image = null;
            }
        }

        const appearance = {
            signature_details: {
                details: [{ type: 'text', value: ' ' }]
            }
        };

        if (base64Image) {
            appearance.background_image = {
                binary: base64Image
            };
        }

        const requestJson = applySignaturePageMode({
            signature_field: {
                location: {
                    page: { number: pageNumber },
                    rectangle: { x, y, width, height }
                },
                appearance
            }
        });


        const response = await signDocument(sessionStorage.getItem('access_token'), pdfFile, requestJson);
      if (!response.ok) {
        const respText = await response.text();
        throw new Error(`Error al firmar: ${response.status} - ${respText}`);
      }
      const respJson = await response.json();
      if (respJson?.tasks?.pending?.[0]?.url) {
        window.location.href = respJson.tasks.pending[0].url;
      }
    } catch (error) {
      setError('Error al validar el PIN. Por favor, inténtelo de nuevo.');
    }
  };



  const callSignDocument = async () => {
    const config = getConfig();
    if (config && config.SIGN_AUTH_REQUIRED ) {
      sessionStorage.setItem('sentToSign', 'true');
    }
    try {
      window.location.href = await authorize(
        process.env.REACT_APP_IDPID,
        process.env.REACT_APP_RESPONSE_TYPE,
        process.env.REACT_APP_STATE,
        process.env.REACT_APP_REDIRECT_URI
      );
    } catch (error) {
      console.error('Authorization failed', error);
    }
  }

  const handleAccept = async () => {
    const selectedCustom = savedAppearances.find((item) => String(item?.id) === option);
    if (option === '0' && !container.current) return;

    try {
            // Validación de X/N
                const X = Number(pageInput);
           const N = Number(numPages || 0);
            if (!Number.isInteger(X) || X <= 0 || (N > 0 && X > N)) {
                setPageError(true);
                setError('error.pageNotExists'); // key i18n
                return; // no cerrar ni publicar
              }

                // Navegar a X en el visor (y fijar manualSignaturePage)
                    try {
                const iframe = document.querySelector('.pdfViewerContainer iframe');
                if (iframe?.contentWindow) {
                    iframe.contentWindow.postMessage({ type: 'NAVIGATE_TO_PAGE', page: X }, '*');
                  }
              } catch {}
      const isCustom = !!selectedCustom;
      sessionStorage.setItem('customAppearanceDefined', isCustom ? 'true' : 'false');

      const iframe = document.querySelector('.pdfViewerContainer iframe');
      let dataUrl = null;
      let cssWidth = 0;
      let cssHeight = 0;

      if (option === "-1") {
        // SIN APARIENCIA: ocultar firma en PDF.js y limpiar preview
        dataUrl = null;
        sessionStorage.removeItem("signatureAppearance");
        sessionStorage.removeItem("signatureAppearanceHtml");
        const appearance = JSON.parse(sessionStorage.getItem('appearance') || '{}');
        if (appearance?.image) {
          appearance.image.value = "";
          sessionStorage.setItem('appearance', JSON.stringify(appearance));
        }
      } else if (option === "0") {
        sessionStorage.removeItem("signatureAppearanceHtml");
        // Captura determinista del nodo tal como se ve (WYSIWYG)
        const div = container.current;
        dataUrl = await captureNodeToPNG(div);

        // Guardamos la preview para el visor
        sessionStorage.setItem("signatureAppearance", dataUrl);

        // Guardamos también en appearance.image.value (sin tocar métricas PDF)
        const appearance = JSON.parse(sessionStorage.getItem('appearance') || '{}');
        appearance.image = appearance.image || {};
        appearance.image.value = dataUrl;
        sessionStorage.setItem('appearance', JSON.stringify(appearance));
        const rect = container.current.getBoundingClientRect();
        cssWidth = Math.round(rect.width);
        cssHeight = Math.round(rect.height);
      } else if (selectedCustom) {
        const configAppearanceRaw = selectedCustom?.configJson?.appearance || {};
        const refreshedAppearance = refreshCustomAppearanceDate(selectedCustom?.htmlTemplate, configAppearanceRaw);
        const configAppearance = refreshedAppearance.configAppearance || {};
        if (refreshedAppearance?.htmlTemplate) {
          try {
            const selectedView = document.querySelector('.apariencia-item-selected .a-view');
            const hostWidth = Math.round(selectedView?.getBoundingClientRect?.().width || 0);
            const regenerated = await renderHtmlTemplateToPNG(refreshedAppearance.htmlTemplate, hostWidth);
            if (regenerated) {
              configAppearance.image = configAppearance.image || {};
              configAppearance.image.value = regenerated;
              try {
                const dims = await getImageDimensionsFromDataUrl(regenerated);
                if (dims?.width > 0 && dims?.height > 0) {
                  const aspect = dims.width / dims.height;
                  const rawHeight = Number(configAppearance?.height);
                  const rect = container.current?.getBoundingClientRect();
                  const normalizedHeight = Number.isFinite(rawHeight) && rawHeight > 0
                      ? Math.round(rawHeight)
                      : Math.round(rect?.height || 131);
                  configAppearance.baseW = dims.width;
                  configAppearance.baseH = dims.height;
                  configAppearance.imageAspect = aspect;
                  configAppearance.height = normalizedHeight;
                  configAppearance.width = Math.max(1, Math.round(normalizedHeight * aspect));
                }
              } catch {}
            }
          } catch (err) {
            console.error('No se pudo regenerar la imagen base64 de la apariencia.', err);
          }
        }
        dataUrl = configAppearance?.image?.value || null;
        if (dataUrl) {
          sessionStorage.setItem("signatureAppearance", dataUrl);
        } else {
          sessionStorage.removeItem("signatureAppearance");
        }
        if (refreshedAppearance?.htmlTemplate) {
          sessionStorage.setItem('signatureAppearanceHtml', refreshedAppearance.htmlTemplate);
        } else {
          sessionStorage.removeItem('signatureAppearanceHtml');
        }
        sessionStorage.setItem('appearance', JSON.stringify(configAppearance));
        const width = Number(configAppearance?.width);
        const height = Number(configAppearance?.height);
        cssWidth = Number.isFinite(width) && width > 0 ? Math.round(width) : 0;
        cssHeight = Number.isFinite(height) && height > 0 ? Math.round(height) : 0;
        if (!cssWidth || !cssHeight) {
          const rect = container.current?.getBoundingClientRect();
          cssWidth = cssWidth || Math.round(rect?.width || 0);
          cssHeight = cssHeight || Math.round(rect?.height || 0);
        }
      }

      // Refrescar la imagen en el visor PDF.js
      if (iframe && iframe.contentWindow) {
        const rect = container.current?.getBoundingClientRect();
        const postWidth = cssWidth || Math.round(rect?.width || 0);
        const postHeight = cssHeight || Math.round(rect?.height || 0);
        iframe.contentWindow.postMessage(
            {
              type: 'UPDATE_SIGNATURE_APPEARANCE',
              imageData: dataUrl,
              cssWidth: postWidth,
              cssHeight: postHeight,
              minScale,
              maxScale
            },
            '*'
        );
      }

      // Cerrar modal (misma lógica que ya tenías)
      const modal = document.getElementById('modalSeleccionarApariencia');
      if (modal) {
        modal.classList.remove('show');
        modal.style.display = 'none';
        unlockBodyAfterModalClose();
      }

    } catch (error) {
      const message = error instanceof Error
          ? error.message
          : 'No se pudieron cargar todos los recursos de la apariencia.';
      alert("No se pudo generar la imagen: " + message);
      console.error(error);
    }
  };


  const handleClose = () => {
    const modal = document.getElementById('modalSeleccionarApariencia');
    if (modal) {
      modal.classList.remove('show');
      modal.style.display = 'none';
      unlockBodyAfterModalClose();
    }
  };

  return (
    <div className="component-modal">

      {/* A partir de aca es el modal */}
      <div className="modal" id="modalSeleccionarApariencia">
        <div className="page-container">

          <div className="content-modal">

            {/* head modal */}
            <div className="header-modal">
              <h2 className="title-modal"> {t('signatureAppearance.signature-appearance')} </h2>
              <span className="close-modal"  onClick={handleClose}> &times; </span>
            </div>
            {/* head modal */}

            {/* body modal */}
            <div className="body-modal">
              {pageError && (
                  <div style={{marginBottom: '12px'}}>
                    <ComponentAlerts alertType="error" alertMessageKey={error || 'error.pageNotExists'}/>

                  </div>
              )}
              <div className="modal-subtitle">
                <h3 className="txt">{t('signatureAppearance.select-option')}</h3>
                <button
                    type="button"
                    data-modal="modalDefinirApariencia"
                    className="btn-medium btn-primary-outline modal-trigger"
                >
                  {t('signatureAppearance.add-appearance')}
                </button>

              </div>

              {/* Apariencias */}
              <div className="container-apariencias">

                <div className="apariencia-item">
                  <div className="a-header">
                    <p>{t('signatureAppearance.no-appearance')}</p>
                    <div className="form-group">
                      <input onChange={handleOptionChange} className="form-checkbox" id="no-appearance" type="radio"
                             name="appearance" value="-1" checked={option === '-1'}/>
                      <label className="form-label" htmlFor="no-appearance"></label>
                    </div>
                  </div>
                  <div className="a-view">
                  </div>
                </div>

                <div className="apariencia-item">
                  <div className="a-header">
                    <p>{t('signatureAppearance.default-appearance')}</p>
                    <div className="form-group">
                      <input onChange={handleOptionChange} className="form-checkbox" id="default-appearance"
                             type="radio" name="appearance" value="0" checked={option === '0'}/>
                      <label className="form-label" htmlFor="default-appearance"></label>
                    </div>
                  </div>
                  <div className="a-view">
                      <div className="signaturePreviewBox">
                       <div ref={container} className="a-content">
                         <SignatureAppearance
                                   logoSrc={logoSrc}
                                   showLogo={true}
                                   signatureSrc={null}
                                   name={getUserName()}
                                   showName={true}
                                   showDate={false}
                                   showFreeText={false}
                                   alignment="center"
                                   logoPosition={getDefaultLogoPositionFromConfigs()}
                                 />
                       </div>
                    </div>
                  </div>
                </div>

                {savedAppearances
                    .filter((item) => (item?.type || '').toUpperCase() === 'CUSTOM')
                    .map((item) => {
                      const isSelected = option === String(item?.id);
                      return (
                          <div key={item?.id} className={`apariencia-item ${isSelected ? 'apariencia-item-selected' : ''}`}>
                            <div className="a-header">
                              <button
                                  type="button"
                                  className="btn-xsmall btn-alternative"
                                  onClick={(e) => handleDeleteAppearance(e, item?.id)}
                              >
                                <span className="material-symbols-outlined">delete</span>
                                {t('signatureAppearance.delete')}
                              </button>
                              <div className="form-group">
                                <input
                                    onChange={handleOptionChange}
                                    className="form-checkbox"
                                    id={`custom-appearance-${item?.id}`}
                                    type="radio"
                                    name="appearance"
                                    value={String(item?.id)}
                                    checked={isSelected}
                                />
                                <label className="form-label" htmlFor={`custom-appearance-${item?.id}`}></label>
                              </div>
                            </div>
                            <div className="a-view">
                              <div className="signaturePreviewBox">
                                <div className="appearance-html-preview__inner"
                                  dangerouslySetInnerHTML={{ __html: item?.htmlTemplate || '' }}
                                />
                              </div>
                            </div>
                          </div>
                      );
                    })}
              </div>
              {/* Fin apariencias */}
              <div className="page-select-container">
                <div className="page-select-row">
                  <label className="txt page-select-label" htmlFor="sig-page-input">
                    {t('signatureAppearance.previewOnPage')}
                  </label>
                   <div className="page-select">
                   <input id="sig-page-input"
                   type="text"
                   inputMode="numeric"
                   pattern="[0-9]*"
                   value={pageInput}
                   onChange={onPageChange}
                   onKeyDown={onPageKeyDown}
                   disabled={allPages}
                   aria-invalid={pageError ? 'true' : 'false'}
                   className={`num-input ${pageError ? 'input-error' : ''}`}
                   />
                   <span className="sep">/</span>
                   <span className="badge-muted">{numPages ?? '—'}</span>
                   </div>
                </div>
                <div className="form-group all-pages-toggle">
                  <input
                      className="form-checkbox"
                      id="all-signature-pages-select"
                      type="checkbox"
                      checked={allPages}
                      onChange={onAllPagesChange}
                  />
                  <label className="form-label" htmlFor="all-signature-pages-select">
                    {t('signatureAppearance.allPages')}
                  </label>
                </div>
              </div>
              <br/>
              <div className="txt"> {t('signatureAppearance.appearance-summary')} </div>
            </div>
            {/* body modal */}

            {/* footer modal */}
            <div className="footer-modal">
              <div className="actions">
                <button type="button" className="btn-small btn-secondary modal-trigger" data-dismiss="modal"
                        onClick={handleClose}> {t('signatureAppearance.cancelar')} </button>
                <button type="button" className="btn-small btn-primary"
                        onClick={handleAccept}> {t('signatureAppearance.accept')} </button>
              </div>
            </div>
            {/* footer modal */}

          </div>

        </div>
      </div>
      {/* Fin del modal */}
      {/* Renderización condicional del segundo modal */}

    </div>
  );
};

export default ModalSeleccionarApariencia;
